/**
 * Create / edit a rate card (manage only). Mirrors the server's zod rules
 * client-side (scope ↔ target, mix sums to 100, positive prices) so the
 * seller sees problems before the round trip. POST for new, PUT for existing.
 * Cancel and ✕ ask before throwing away changes (`confirmDiscard`).
 */

import React, { useId } from 'react';
import { Button, Modal, Switch } from '../../ui';
import UnitMixEditor from '../MediaPlans/UnitMixEditor';
import type { AdvertiserRecord, MediaPlanAgency, MediaPlanCategory, MediaPlanUnitDef, MediaPlanUnitKey, RateCardScope, RateCardStatus, TargetingLayerKey } from '../MediaPlans/types';
import { MEDIA_PLAN_UNIT_ORDER } from '../MediaPlans/types';
import { CARD_STATUS_LABELS, LAYER_LABELS, SCOPE_LABELS, nextId, type RateCardDraft } from './rateCardDraft';
import { bannerStyle, fieldStyle, gridStyle, hintStyle, labelStyle, mutedText, numberFieldStyle, sectionTitleStyle } from '../MediaPlans/formStyles';

interface Props {
  draft: RateCardDraft;
  onChange: (next: RateCardDraft) => void;
  onSave: () => void;
  /** Cancel or ✕: the parent closes, or asks first when there are changes. */
  onCancel: () => void;
  /** Show "Discard your changes?" in the footer. */
  confirmDiscard?: boolean;
  onDiscard?: () => void;
  onKeepEditing?: () => void;
  saving: boolean;
  errors: string[];
  units: MediaPlanUnitDef[];
  categories: MediaPlanCategory[];
  agencies: MediaPlanAgency[];
  advertisers: AdvertiserRecord[];
}

const SCOPES: RateCardScope[] = ['default', 'category', 'agency', 'advertiser'];
const STATUSES: RateCardStatus[] = ['draft', 'active', 'archived'];
const LAYERS: TargetingLayerKey[] = ['demo', 'culture-stack', 'geo', 'daypart', 'competitive'];

const RateCardEditor: React.FC<Props> = ({ draft, onChange, onSave, onCancel, confirmDiscard = false, onDiscard, onKeepEditing, saving, errors, units, categories, agencies, advertisers }) => {
  const id = useId();
  const set = <K extends keyof RateCardDraft>(key: K, value: RateCardDraft[K]) => onChange({ ...draft, [key]: value });
  const setUnit = (k: MediaPlanUnitKey, patch: Partial<RateCardDraft['units'][MediaPlanUnitKey]>) => onChange({ ...draft, units: { ...draft.units, [k]: { ...draft.units[k], ...patch } } });
  const unitLabel = (k: MediaPlanUnitKey) => units.find((u) => u.key === k)?.shortLabel || k;
  const sortedAdvertisers = [...advertisers].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Modal
      open
      onClose={onCancel}
      eyebrow={draft.id ? `Editing ${draft.id}` : 'New rate card'}
      title={draft.name.trim() || 'Rate card'}
      width={840}
      closeOnEsc={false}
      closeOnBackdrop={false}
      footer={
        confirmDiscard ? (
          <div role="group" aria-label="Discard changes" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-1)' }}>Discard your changes to this card?</span>
            <Button variant="ghost" onClick={onKeepEditing}>
              Keep editing
            </Button>
            <Button variant="destructive" onClick={onDiscard}>
              Discard changes
            </Button>
          </div>
        ) : (
          <>
            <Button variant="ghost" onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={onSave} disabled={saving}>
              {saving ? 'Saving…' : draft.id ? 'Save changes' : 'Create rate card'}
            </Button>
          </>
        )
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <section style={styles.section}>
          <h4 style={sectionTitleStyle}>Card</h4>
          <div style={gridStyle}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label htmlFor={`${id}-name`} style={labelStyle}>Name</label>
              <input id={`${id}-name`} style={fieldStyle} value={draft.name} maxLength={120} disabled={saving} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Restaurants & QSR 2026" />
            </div>
            <div>
              <label htmlFor={`${id}-scope`} style={labelStyle}>Scope</label>
              <select id={`${id}-scope`} style={{ ...fieldStyle, cursor: 'pointer' }} value={draft.scope} disabled={saving} onChange={(e) => set('scope', e.target.value as RateCardScope)}>
                {SCOPES.map((s) => (
                  <option key={s} value={s}>{SCOPE_LABELS[s]}</option>
                ))}
              </select>
              <div style={hintStyle}>Advertiser › agency › category › default — the most specific active card prices each unit.</div>
            </div>
            {draft.scope === 'category' && (
              <div>
                <label htmlFor={`${id}-cat`} style={labelStyle}>Category</label>
                <select id={`${id}-cat`} style={{ ...fieldStyle, cursor: 'pointer' }} value={draft.categoryKey} disabled={saving} onChange={(e) => set('categoryKey', e.target.value)}>
                  <option value="">Choose…</option>
                  {[...categories].sort((a, b) => a.label.localeCompare(b.label)).map((c) => (
                    <option key={c.key} value={c.key}>{c.label}</option>
                  ))}
                </select>
              </div>
            )}
            {draft.scope === 'agency' && (
              <div>
                <label htmlFor={`${id}-agency`} style={labelStyle}>Agency</label>
                <select id={`${id}-agency`} style={{ ...fieldStyle, cursor: 'pointer' }} value={draft.agencyKey} disabled={saving} onChange={(e) => set('agencyKey', e.target.value)}>
                  <option value="">Choose…</option>
                  {agencies.map((a) => (
                    <option key={a.key} value={a.key}>{a.name}</option>
                  ))}
                </select>
              </div>
            )}
            {draft.scope === 'advertiser' && (
              <div>
                <label htmlFor={`${id}-adv`} style={labelStyle}>Advertiser</label>
                <select id={`${id}-adv`} style={{ ...fieldStyle, cursor: 'pointer' }} value={draft.advertiserKey} disabled={saving} onChange={(e) => set('advertiserKey', e.target.value)}>
                  <option value="">Choose…</option>
                  {sortedAdvertisers.map((a) => (
                    <option key={a.key} value={a.key}>{a.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label htmlFor={`${id}-status`} style={labelStyle}>Status</label>
              <select id={`${id}-status`} style={{ ...fieldStyle, cursor: 'pointer' }} value={draft.status} disabled={saving} onChange={(e) => set('status', e.target.value as RateCardStatus)}>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{CARD_STATUS_LABELS[s]}</option>
                ))}
              </select>
              <div style={hintStyle}>Only active cards price plans.</div>
            </div>
            <div>
              <label htmlFor={`${id}-from`} style={labelStyle}>Effective from</label>
              <input id={`${id}-from`} type="date" style={fieldStyle} value={draft.effectiveFrom} disabled={saving} onChange={(e) => set('effectiveFrom', e.target.value)} />
            </div>
            <div>
              <label htmlFor={`${id}-to`} style={labelStyle}>Effective to <span style={styles.optional}>(optional)</span></label>
              <input id={`${id}-to`} type="date" style={fieldStyle} value={draft.effectiveTo} min={draft.effectiveFrom || undefined} disabled={saving} onChange={(e) => set('effectiveTo', e.target.value)} />
            </div>
          </div>
        </section>

        <section style={styles.section}>
          <h4 style={sectionTitleStyle}>Unit prices</h4>
          <div style={hintStyle}>Leave a unit blank to fall through to the next card. CPMs are base rates before targeting upcharges.</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {['Ad unit', 'CPM ($)', 'Flat ($)', 'Per', 'Floor CPM', 'Note'].map((h) => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MEDIA_PLAN_UNIT_ORDER.map((k) => {
                  const u = draft.units[k];
                  const def = units.find((x) => x.key === k);
                  const allowsCpm = !def || def.pricing !== 'flat';
                  const allowsFlat = !def || def.pricing !== 'cpm';
                  return (
                    <tr key={k}>
                      <td style={styles.td}>
                        <div style={{ fontWeight: 600 }}>{def?.label || k}</div>
                        <div style={mutedText}>{def?.platformLabel}</div>
                      </td>
                      <td style={styles.td}>
                        <input type="number" min={0} step={0.25} inputMode="decimal" aria-label={`${def?.label || k} CPM`} style={{ ...numberFieldStyle, width: 96 }} value={u.cpm} disabled={saving || !allowsCpm} onChange={(e) => setUnit(k, { cpm: e.target.value })} />
                      </td>
                      <td style={styles.td}>
                        <input type="number" min={0} step={50} inputMode="decimal" aria-label={`${def?.label || k} flat rate`} style={{ ...numberFieldStyle, width: 104 }} value={u.flatUsd} disabled={saving || !allowsFlat} onChange={(e) => setUnit(k, { flatUsd: e.target.value })} />
                      </td>
                      <td style={styles.td}>
                        <select aria-label={`${def?.label || k} flat rate period`} style={{ ...fieldStyle, width: 96, cursor: 'pointer' }} value={u.flatPer} disabled={saving || !allowsFlat} onChange={(e) => setUnit(k, { flatPer: e.target.value as '' | 'week' | 'flight' })}>
                          <option value="">—</option>
                          <option value="week">week</option>
                          <option value="flight">flight</option>
                        </select>
                      </td>
                      <td style={styles.td}>
                        <input type="number" min={0} step={0.25} inputMode="decimal" aria-label={`${def?.label || k} floor CPM`} style={{ ...numberFieldStyle, width: 96 }} value={u.floorCpm} disabled={saving} onChange={(e) => setUnit(k, { floorCpm: e.target.value })} />
                      </td>
                      <td style={styles.td}>
                        <input aria-label={`${def?.label || k} note`} style={{ ...fieldStyle, minWidth: 180 }} value={u.note} maxLength={200} disabled={saving} onChange={(e) => setUnit(k, { note: e.target.value })} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <section style={styles.section}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <h4 style={sectionTitleStyle}>Targeting upcharges</h4>
            <Button size="sm" variant="subtle" disabled={saving} onClick={() => set('upcharges', [...draft.upcharges, { id: nextId('up'), layer: 'demo', label: '', cpmAdd: '1.75', appliesTo: [], freeWhenAgeOnly: false, note: '' }])}>
              + Add upcharge
            </Button>
          </div>
          {draft.upcharges.length === 0 && <div style={mutedText}>None — the next card down supplies upcharges.</div>}
          {draft.upcharges.map((u, i) => (
            <div key={u.id} style={styles.upRow}>
              <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr 110px', gap: 8 }}>
                <div>
                  <label style={labelStyle}>Layer</label>
                  <select aria-label={`Upcharge ${i + 1} layer`} style={{ ...fieldStyle, cursor: 'pointer' }} value={u.layer} disabled={saving} onChange={(e) => set('upcharges', draft.upcharges.map((x) => (x.id === u.id ? { ...x, layer: e.target.value as TargetingLayerKey } : x)))}>
                    {LAYERS.map((l) => (
                      <option key={l} value={l}>{LAYER_LABELS[l]}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Label</label>
                  <input aria-label={`Upcharge ${i + 1} label`} style={fieldStyle} value={u.label} maxLength={80} placeholder="Demo targeting" disabled={saving} onChange={(e) => set('upcharges', draft.upcharges.map((x) => (x.id === u.id ? { ...x, label: e.target.value } : x)))} />
                </div>
                <div>
                  <label style={labelStyle}>+ CPM ($)</label>
                  <input type="number" min={0} step={0.25} inputMode="decimal" aria-label={`Upcharge ${i + 1} CPM add`} style={numberFieldStyle} value={u.cpmAdd} disabled={saving} onChange={(e) => set('upcharges', draft.upcharges.map((x) => (x.id === u.id ? { ...x, cpmAdd: e.target.value } : x)))} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={mutedText}>Applies to:</span>
                {MEDIA_PLAN_UNIT_ORDER.map((k) => (
                  <label key={k} style={styles.check}>
                    <input
                      type="checkbox"
                      checked={u.appliesTo.includes(k)}
                      disabled={saving}
                      onChange={(e) => set('upcharges', draft.upcharges.map((x) => (x.id === u.id ? { ...x, appliesTo: e.target.checked ? [...x.appliesTo, k] : x.appliesTo.filter((y) => y !== k) } : x)))}
                    />
                    {unitLabel(k)}
                  </label>
                ))}
                <span style={mutedText}>(none = every CPM unit)</span>
              </div>
              <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                {u.layer === 'demo' && (
                  <label style={styles.check}>
                    <input type="checkbox" checked={u.freeWhenAgeOnly} disabled={saving} onChange={(e) => set('upcharges', draft.upcharges.map((x) => (x.id === u.id ? { ...x, freeWhenAgeOnly: e.target.checked } : x)))} />
                    Waived for age-only demos (A25-54)
                  </label>
                )}
                <input aria-label={`Upcharge ${i + 1} note`} style={{ ...fieldStyle, flex: 1, minWidth: 200 }} value={u.note} maxLength={200} placeholder="Note (optional)" disabled={saving} onChange={(e) => set('upcharges', draft.upcharges.map((x) => (x.id === u.id ? { ...x, note: e.target.value } : x)))} />
                <Button size="sm" variant="ghost" disabled={saving} onClick={() => set('upcharges', draft.upcharges.filter((x) => x.id !== u.id))} aria-label={`Remove upcharge ${i + 1}`}>
                  Remove
                </Button>
              </div>
            </div>
          ))}
        </section>

        <section style={styles.section}>
          <h4 style={sectionTitleStyle}>Added value</h4>
          <Switch checked={draft.hasAddedValue} disabled={saving} onChange={(on) => set('hasAddedValue', on)} label="This card sets an added value policy" description="Off: plans take added value from the next card down." />
          {draft.hasAddedValue && (
            <>
              <div style={gridStyle}>
                <div>
                  <label htmlFor={`${id}-bonus`} style={labelStyle}>Bonus impressions (%)</label>
                  <input id={`${id}-bonus`} type="number" min={0} max={100} step={1} inputMode="decimal" style={numberFieldStyle} value={draft.bonusPct} disabled={saving} onChange={(e) => set('bonusPct', e.target.value)} />
                </div>
                <div>
                  <div style={labelStyle}>Bonus runs on</div>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {MEDIA_PLAN_UNIT_ORDER.filter((k) => k !== 'sov-takeover').map((k) => (
                      <label key={k} style={styles.check}>
                        <input type="checkbox" checked={draft.bonusUnits.includes(k)} disabled={saving} onChange={(e) => set('bonusUnits', e.target.checked ? [...draft.bonusUnits, k] : draft.bonusUnits.filter((y) => y !== k))} />
                        {unitLabel(k)}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={labelStyle}>Budget-tier extras</div>
                  <Button size="sm" variant="subtle" disabled={saving} onClick={() => set('customItems', [...draft.customItems, { id: nextId('ci'), minBudgetUsd: '400000', label: '', valueUsd: '' }])}>
                    + Add item
                  </Button>
                </div>
                {draft.customItems.map((c, i) => (
                  <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '140px 1fr 130px auto', gap: 8, alignItems: 'end', marginTop: 6 }}>
                    <div>
                      <label style={labelStyle}>From budget ($)</label>
                      <input type="number" min={0} step={1000} inputMode="decimal" aria-label={`Added-value item ${i + 1} minimum budget`} style={numberFieldStyle} value={c.minBudgetUsd} disabled={saving} onChange={(e) => set('customItems', draft.customItems.map((x) => (x.id === c.id ? { ...x, minBudgetUsd: e.target.value } : x)))} />
                    </div>
                    <div>
                      <label style={labelStyle}>Item</label>
                      <input aria-label={`Added-value item ${i + 1} label`} style={fieldStyle} value={c.label} maxLength={120} placeholder="Branded Lifestyle Segment" disabled={saving} onChange={(e) => set('customItems', draft.customItems.map((x) => (x.id === c.id ? { ...x, label: e.target.value } : x)))} />
                    </div>
                    <div>
                      <label style={labelStyle}>Value ($, optional)</label>
                      <input type="number" min={0} step={500} inputMode="decimal" aria-label={`Added-value item ${i + 1} value`} style={numberFieldStyle} value={c.valueUsd} disabled={saving} onChange={(e) => set('customItems', draft.customItems.map((x) => (x.id === c.id ? { ...x, valueUsd: e.target.value } : x)))} />
                    </div>
                    <Button size="sm" variant="ghost" disabled={saving} onClick={() => set('customItems', draft.customItems.filter((x) => x.id !== c.id))} aria-label={`Remove added-value item ${i + 1}`}>
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
              <div>
                <label htmlFor={`${id}-avnotes`} style={labelStyle}>Added value notes</label>
                <input id={`${id}-avnotes`} style={fieldStyle} value={draft.addedValueNotes} maxLength={300} disabled={saving} onChange={(e) => set('addedValueNotes', e.target.value)} />
              </div>
            </>
          )}
        </section>

        <section style={styles.section}>
          <h4 style={sectionTitleStyle}>Default unit mix</h4>
          <Switch checked={draft.hasDefaultMix} disabled={saving} onChange={(on) => set('hasDefaultMix', on)} label="This card sets a default unit mix" description="Used when a plan doesn't choose its own mix." />
          {draft.hasDefaultMix && <UnitMixEditor units={units} mix={draft.defaultUnitMix} onChange={(m) => set('defaultUnitMix', m)} disabled={saving} compact />}
          <div style={gridStyle}>
            <div>
              <label htmlFor={`${id}-min`} style={labelStyle}>Minimum budget ($) <span style={styles.optional}>(optional)</span></label>
              <input id={`${id}-min`} type="number" min={0} step={1000} inputMode="decimal" style={numberFieldStyle} value={draft.minimumBudgetUsd} disabled={saving} onChange={(e) => set('minimumBudgetUsd', e.target.value)} />
            </div>
          </div>
          <div>
            <label htmlFor={`${id}-notes`} style={labelStyle}>Notes</label>
            <textarea id={`${id}-notes`} style={{ ...fieldStyle, minHeight: 60, resize: 'vertical', fontFamily: 'var(--font-sans)' }} value={draft.notes} maxLength={2000} disabled={saving} onChange={(e) => set('notes', e.target.value)} />
          </div>
        </section>

        {errors.length > 0 && (
          <div role="alert" style={bannerStyle('danger')}>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
};

const styles: Record<string, React.CSSProperties> = {
  section: { display: 'flex', flexDirection: 'column', gap: 10 },
  optional: { fontWeight: 400, textTransform: 'none', letterSpacing: 0 },
  th: { textAlign: 'left', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--fg-4)', padding: '4px 6px', borderBottom: '1px solid var(--border-subtle)' },
  td: { padding: '6px 6px', verticalAlign: 'top' },
  upRow: { display: 'flex', flexDirection: 'column', gap: 8, padding: 12, borderRadius: 'var(--radius-md)', background: 'var(--bg-surface-2)' },
  check: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--fg-2)', cursor: 'pointer' },
};

export default RateCardEditor;
